<template>
  <div class="page-container">
    <div ref="techManagerPage" class="tech-manager-page" :class="{ 'locale-zh-CN': isZhLocale }">
      <!-- 页面头部 -->
      <PageHeader :config="config" @module-click="handleModuleClick" />

      <!-- 面包屑导航 -->
      <Breadcrumb :items="breadcrumbItems" />

      <!-- 头部Banner -->
      <SearchBanner 
        :title-main1="managerConfig.techManager.title1"
        :title-main2="managerConfig.techManager.title2"
        :subtitle="managerConfig.techManager.subtitle"
        :placeholder="managerConfig.techManager.searchPlaceholder"
        :button-text="managerConfig.techManager.searchButton"
        :keyword.sync="searchKeyword"
        :background-image="localeImg('banner/jishujingliren.png')"
        search-object="manager"
        @search="handleSearch"
      />

      <!-- 主体内容区 -->
      <div class="main-content" ref="managerSection">
        <!-- 筛选条件区 -->
        <div class="filter-section">
          <!-- 领域筛选 -->
          <div class="filter-row">
            <div class="filter-item">
              <span class="filter-label">{{ managerConfig.techManager.filters.domain }}</span>
              <div class="filter-tags">
                <div 
                  v-for="(item, index) in domainOptions" 
                  :key="'domain-' + index"
                  class="filter-tag"
                  :class="{ active: selectedDomain === item.value }"
                  @click="handleFilterChange('domain', item.value)"
                >
                  {{ item.label }}
                </div>
              </div>
            </div>
          </div>

          <!-- 服务地区筛选 -->
          <div class="filter-row">
            <div class="filter-item">
              <span class="filter-label">{{ managerConfig.techManager.filters.province }}</span>
              <div class="filter-tags">
                <div 
                  v-for="(item, index) in provinceOptions" 
                  :key="'province-' + index"
                  class="filter-tag"
                  :class="{ active: selectedProvince === item.value }"
                  @click="handleFilterChange('province', item.value)"
                >
                  {{ item.label }}
                </div>
              </div>
            </div>
          </div>

       
        </div>

        <!-- 经理人列表区 -->
        <div v-if="loading" class="loading">{{ getText('common.loading') }}</div>
        <TalentList 
          v-else
          :talent-list="managerList"
          type="manager"
          :columns="4"
          @talent-click="handleManagerClick"
        />

        <!-- 分页 -->
        <div class="pagination">
          <span class="page-info">
            {{ managerConfig.techManager.pagination.pageInfo
                .replace('{total}', totalCount)
                .replace('{pageSize}', pageSize) }}
          </span>
          <div class="page-controls">
            <button
              class="page-btn"
              :disabled="currentPage === 1"
              @click="changePage(1)"
            >{{ managerConfig.techManager.pagination.firstPage || '' }}</button>
            <button 
              class="page-btn" 
              :disabled="currentPage === 1"
              @click="changePage(currentPage - 1)"
            >{{ managerConfig.techManager.pagination.prevPage }}</button>
            <button 
              v-for="page in visiblePages" 
              :key="page"
              :class="['page-num', { active: page === currentPage }]"
              @click="changePage(page)"
            >{{ page }}</button>
            <button 
              class="page-btn" 
              :disabled="currentPage === totalPages"
              @click="changePage(currentPage + 1)"
            >{{ managerConfig.techManager.pagination.nextPage }}</button>
            <button
              class="page-btn"
              :disabled="currentPage === totalPages"
              @click="changePage(totalPages)"
            >{{ managerConfig.techManager.pagination.lastPage || '' }}</button>
          </div>
        </div>
      </div>

      <!-- 页脚 -->
      <PageFooter />
    </div>
  </div>
</template>

<script>
import { applyPageScale, setupPageScaleObserver, teardownPageScaleObserver } from '@/utils/pageScale'
import { localeImg } from '@/utils/localeAsset'
import { getHomeConfig } from '@/config/homeConfig'
import { getTechManagerConfig } from '@/config/techManagerConfig'
import PageHeader from '@/components/PageHeader.vue'
import PageFooter from '@/components/PageFooter.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'
import SearchBanner from '@/components/SearchBanner.vue'
import TalentList from '@/components/TalentList.vue'
import techManagerApi from '@/api/techManager'
import supplyDemandApi from '@/api/supplyDemand'
import { getCurrentLocale, getText as i18nGetText } from '@/utils/i18nText'

export default {
  name: 'TechManager',
  components: {
    PageFooter,
    PageHeader,
    Breadcrumb,
    SearchBanner,
    TalentList
  },
  data() {
    return {
      config: {},
      managerConfig: {},
      searchKeyword: '',
      
      // 面包屑
      breadcrumbItems: [],
      
      // 筛选条件
      selectedDomain: 'all',
      selectedProvince: 'all',
      selectedSort: 'all',

      // 字典筛选项（value 传 itemCode/id）
      domainOptions: [{ label: '全部', value: 'all' }],
      provinceOptions: [{ label: '全部', value: 'all' }],
      
      // 经理人列表数据
      managerList: [],
      loading: false,
      
      // 分页相关
      currentPage: 1,
      pageSize: 12,
      totalCount: 0
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalCount / this.pageSize)
    },
    
    visiblePages() {
      const total = this.totalPages
      const current = this.currentPage
      const max = 6
      if (!total || total <= max) {
        return Array.from({ length: total || 1 }, (_, i) => i + 1)
      }
      let start = Math.max(1, current - 2)
      let end = start + max - 1
      if (end > total) {
        end = total
        start = end - max + 1
      }
      const pages = []
      for (let i = start; i <= end; i++) pages.push(i)
      return pages
    }
  },
  created() {
    this.loadConfig()
  },
  watch: {
    selectedDomain() {
      this.currentPage = 1
      this.loadManagerList()
    },
    selectedProvince() {
      this.currentPage = 1
      this.loadManagerList()
    },
    selectedSort() {
      this.currentPage = 1
      this.loadManagerList()
    },
    currentPage() {
      this.loadManagerList()
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.setScale()
      this.observePageResize()
    })
    window.addEventListener('resize', this.setScale)
  },
  beforeDestroy() {
    teardownPageScaleObserver(this)
    window.removeEventListener('resize', this.setScale)
  },
  methods: {
    setScale() {
      applyPageScale(this.getPageScaleEl())
    },

    getPageScaleEl() {
      return this.$refs.techManagerPage
    },

    observePageResize() {
      setupPageScaleObserver(this, () => this.getPageScaleEl())
    },
    getText(key) {
      return i18nGetText(key)
    },
    
    async loadConfig() {
      // 加载首页配置（用于头部和页脚）
      this.config = getHomeConfig(getCurrentLocale())
      // 加载技术经理人配置
      this.managerConfig = getTechManagerConfig(getCurrentLocale())
      
      // 初始化面包屑
      const breadcrumb = this.managerConfig.techManager?.breadcrumb || {}
      this.breadcrumbItems = [
        { label: breadcrumb.home || '', path: '/index' },
        { label: breadcrumb.current || '', path: '' }
      ]
      
      // 加载字典筛选项（yField/fjkjtarea）
      await this.loadDictData()

      // 初始化筛选条件
      if (this.managerConfig.techManager) {
        this.selectedSort = this.managerConfig.techManager.sortList?.[0] || ''
      }
      this.selectedDomain = this.domainOptions[0]?.value || ''
      this.selectedProvince = this.provinceOptions[0]?.value || ''
      
      // 加载经理人列表
      this.loadManagerList()
    },

    // 加载字典数据：yField（擅长领域）、fjkjtarea（省份/地区）
    async loadDictData() {
      try {
        const response = await supplyDemandApi.getDictData(['yField', 'fjkjtarea'])
        const result = response.data || {}

        if (result.status === 0) {
          // 兼容多种返回结构
          let dictData = []
          if (result.data && Array.isArray(result.data.rows)) {
            dictData = result.data.rows
          } else if (Array.isArray(result.data)) {
            dictData = result.data
          } else if (typeof result.data === 'object' && result.data !== null) {
            dictData = Object.values(result.data).flat()
          }
          if (!Array.isArray(dictData)) dictData = []

          // yField：擅长领域（传 itemCode/id）
          const fieldDict = dictData
            .filter(item => item && item.dictCode === 'yField')
            .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))
          this.domainOptions = [
            { label: '全部', value: 'all' },
            ...fieldDict.map(item => ({
              label: item.itemValue || '',
              value: item.itemCode || ''
            }))
          ]

          // fjkjtarea：省份（只取一级）
          const provinceDict = dictData
            .filter(item => {
              if (!item || item.dictCode !== 'fjkjtarea') return false
              // 兼容两种约定：parentCode 为空 或 parentCode 为 0/'0'
              return !item.parentCode || item.parentCode === '0' || item.parentCode === 0
            })
            .sort((a, b) => (a.sortNum || 0) - (b.sortNum || 0))
          this.provinceOptions = [
            { label: '全部', value: 'all' },
            ...provinceDict.map(item => ({
              label: item.itemValue || '',
              value: item.itemCode || ''
            }))
          ]
        } else {
          // 接口失败：回退到配置
          this.domainOptions = (this.managerConfig.techManager?.domainList || []).map(item => ({
            label: typeof item === 'string' ? item : (item.label || item.value || ''),
            value: typeof item === 'string' ? item : (item.value || item.itemCode || '')
          }))
          if (!this.domainOptions.length) this.domainOptions = [{ label: '全部', value: 'all' }]

          this.provinceOptions = (this.managerConfig.techManager?.provinceList || []).map(item => ({
            label: typeof item === 'string' ? item : (item.label || item.value || ''),
            value: typeof item === 'string' ? item : (item.value || item.itemCode || '')
          }))
          if (!this.provinceOptions.length) this.provinceOptions = [{ label: '全部', value: 'all' }]
        }
      } catch (e) {
        console.error('加载字典数据失败:', e)
        // 回退到配置
        this.domainOptions = (this.managerConfig.techManager?.domainList || []).map(item => ({
          label: typeof item === 'string' ? item : (item.label || item.value || ''),
          value: typeof item === 'string' ? item : (item.value || item.itemCode || '')
        }))
        if (!this.domainOptions.length) this.domainOptions = [{ label: '全部', value: 'all' }]

        this.provinceOptions = (this.managerConfig.techManager?.provinceList || []).map(item => ({
          label: typeof item === 'string' ? item : (item.label || item.value || ''),
          value: typeof item === 'string' ? item : (item.value || item.itemCode || '')
        }))
        if (!this.provinceOptions.length) this.provinceOptions = [{ label: '全部', value: 'all' }]
      }
    },

    // 加载技术经理人列表
    async loadManagerList() {
      this.loading = true
      try {
        const params = {
          pageNum: this.currentPage,
          pageSize: this.pageSize,
          keyword: this.searchKeyword || '',
          appDomainType: this.selectedDomain === 'all' ? '' : this.selectedDomain,
          provinceId: this.selectedProvince === 'all' ? '' : this.selectedProvince,
          serviceArea: this.selectedProvince === 'all' ? '' : this.selectedProvince
        }
        
        const response = await techManagerApi.getManagerList(params)
        const result = response.data || {}
        if (result.status === 0 && result.data) {
          const list = result.data.rows || []
          const defaultAvatar = this.managerConfig.techManager?.assets?.avatarImg || ''
          
          this.managerList = list.map(item => {
            // 处理头像（facePhoto是fileid，需要通过API转换为图片URL）
            let avatar = defaultAvatar
            if (item.facePhoto) {
              avatar = techManagerApi.getImageUrl(item.facePhoto)
            }
            
            // 处理标签（tecDomainTypeName可能是用 / 分隔的多个标签）
            let tags = []
            if (item.tecDomainTypeName) {
              tags = item.tecDomainTypeName.split('/').map(tag => tag.trim()).filter(Boolean)
            }
            
            return {
              id: item.id,
              name: item.name || '',
              tags: tags,
              views: item.viewCount || 0,
              rating: 0, // API返回中没有评分字段，保持为0或根据业务需求计算
              avatar: avatar,
              post: item.post || '',
              title: item.title || '',
              unit: item.unit || '',
              serviceArea: item.serviceAreaName || '',
              workYears: item.workYears || 0
            }
          })
          
          // 更新总数
          this.totalCount = result.data.totalCount || 0
        } else {
          this.managerList = []
          this.totalCount = 0
        }
      } catch (error) {
        console.error('加载技术经理人列表失败:', error)
        this.managerList = []
        this.totalCount = 0
      } finally {
        this.loading = false
      }
    },
    
    handleModuleClick(module) {
      if (module.id === 'home') {
        this.$router.push('/index')
      } else if (module.id === 'innovation') {
        this.$router.push('/innovation')
      } else if (module.id === 'supply') {
        this.$router.push('/supply-demand')
      }
    },
    
    handleSearch() {
      console.log('搜索:', this.searchKeyword)
      this.currentPage = 1
      this.loadManagerList()
    },
    
    handleManagerClick(manager) {
      this.$router.push({
        path: '/manager/detail',
        query: { id: manager.id }
      })
    },
    
    // 筛选条件变化
    handleFilterChange(type, value) {
      if (type === 'domain') {
        this.selectedDomain = value
      } else if (type === 'province') {
        this.selectedProvince = value
      } else if (type === 'sort') {
        this.selectedSort = value
      }
      // watch 会自动触发 loadManagerList
    },
    
    changePage(page) {
      if (page < 1 || page > this.totalPages) return
      this.currentPage = page
      // watch 会自动触发 loadManagerList
      const managerSection = this.$refs.managerSection
      if (managerSection) {
        managerSection.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.page-container {
  width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.tech-manager-page {
  width: 1920px;
  min-height: 100vh;
  background: #fff;
  transform-origin: top left;
}

/* 主体内容区 */
.main-content {
  width: 1700px;
  margin: 0 auto;
  padding: 20px 0;
  
  .loading {
    text-align: center;
    padding: 40px;
    font-size: 14px;
    color: #666666;
  }
}

/* 筛选条件区 */
.filter-section {
  background: #FFFFFF;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 20px;
  margin-bottom: 20px;
}

.filter-row {
  margin-bottom: 15px;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.filter-item {
  display: flex;
  align-items: flex-start;
}

.filter-label {
  font-size: 14px;
  color: #333333;
  margin-right: 15px;
  width: 80px;
  padding-top: 8px;
  flex-shrink: 0;
}

.filter-tags {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.filter-tag {
  padding: 6px 15px;
  background: #FFFFFF;
  border: 1px solid #DDDDDD;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 13px;
  color: #666666;
  line-height: 1.4;
  
  &:hover {
    background: #FFF8E6;
    border-color: #A0903C;
    color: #A0903C;
  }
  
  &.active {
    background: #A0903C;
    border-color: #A0903C;
    color: #FFFFFF;
  }
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #FFFFFF;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 20px 30px;
  margin-bottom: 40px;
}

.page-info {
  font-size: 13px;
  color: #666666;
}

.page-controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-btn,
.page-num {
  min-width: 32px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #DDDDDD;
  background: #FFFFFF;
  border-radius: 4px;
  font-size: 13px;
  color: #666666;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover:not(:disabled):not(.active) {
    border-color: #A0903C;
    color: #A0903C;
    background: #FFF8E6;
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
    background: #F5F5F5;
  }
  
  &.active {
    background: #A0903C;
    border-color: #A0903C;
    color: #FFFFFF;
    font-weight: 500;
  }
}




@import '@/assets/scss/mixins/portal-filter-locale-zh-cn.scss';

.tech-manager-page.locale-zh-CN {
  @include portal-filter-locale-zh-cn;
}
</style>
